import { asArray } from "@/lib/asArray";
import { PdfJsViewer } from "@/components/PdfJsViewer";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { waitForJob } from "@/lib/waitJob";   // Batch 27 · Add-on 2 Step 2 · Q3
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LockedBanner } from "@/components/SignOffCard";
import AILabel from "@/components/AILabel";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/dt";
import { Upload, Loader2, Sparkles, Trash2, Check, X, FileText, Download, Search, ExternalLink } from "lucide-react";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { DOC_REVIEW_ACCEPT, splitDocReviewFiles } from "@/lib/docReviewFiles";   // Batch 33 · T7
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const STATUS_CLS = { none: "bg-slate-100 text-slate-700", draft: "bg-amber-100 text-amber-800", accepted: "bg-emerald-100 text-emerald-800" };

async function downloadBlob(url, body, fallbackName) {
  const r = await api.post(url, body, { responseType: "blob" });
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const href = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = href; a.download = name; a.click();
  URL.revokeObjectURL(href);
}

export default function DocSummaries() {
  const { t, lang } = useI18n();
  const { current } = useEngagement();
  const engId = current?.id;
  const locked = !!current?.signed_off;
  const [docs, setDocs] = useState([]);
  const [sums, setSums] = useState([]);
  const [selId, setSelId] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [estimate, setEstimate] = useState(null); // {items:[{document_id,filename,pages,estimated_units,too_large}], running}
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null); // {docId, page, url}
  const fileRef = useRef(null);

  const load = async () => {
    if (!engId) return;
    try {
      const [d, s] = await Promise.all([api.get(`/engagements/${engId}/doc-summaries/documents`), api.get(`/engagements/${engId}/doc-summaries`)]);
      setDocs(d.data || []); setSums(s.data || []);
    } catch (e) { toast.error(toErrMsg(e, t("docsum.loadFailed"))); }
  };
  const loadOnChange = useEffectEvent(load);
  useEffect(() => { loadOnChange(); }, [engId, loadOnChange]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return docs.filter((d) => (statusF === "all" || d.summary_status === statusF) &&
      (!qq || `${d.original_filename || ""} ${d.doc_ref || ""} ${d.doc_type || ""}`.toLowerCase().includes(qq)));
  }, [docs, q, statusF]);
  const sel = docs.find((d) => d.id === selId) || null;
  const selSums = useMemo(() => sums.filter((s) => s.document_id === selId).sort((a, b) => b.version - a.version), [sums, selId]);

  const uploadMany = async (files) => {
    // Batch 33 · T7: only PDF / images go to /documents/upload — any other file is not sent, and the page says where it goes
    const { accepted: list, rejected } = splitDocReviewFiles(Array.from(files || []));
    if (rejected.length) toast.warning(t("b33ui.filesNotAdded").replace("{n}", String(rejected.length)));
    if (!list.length) { if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true);
    let ok = 0;
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f); fd.append("engagement_id", engId); fd.append("doc_type", "auto");
      try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
      catch (e) {
        const d = e?.response?.data;
        if (e?.response?.status === 409 && d?.error_code === "DOCUMENT_DUPLICATE") {
          const ref = d?.detail?.duplicate_of || "";
          if (await askConfirm(t("docsExtras.duplicatePrompt").replace("{ref}", ref))) {
            fd.append("force", "1");
            try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
            catch (e2) { toast.error(`${f.name}: ${toErrMsg(e2, t("docsum.uploadFailed"))}`); }
          }
        } else toast.error(`${f.name}: ${toErrMsg(e, t("docsum.uploadFailed"))}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (ok) toast.success(t("docsum.uploaded").replace("{n}", String(ok)));
    await load();
  };

  const openEstimate = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const items = await Promise.all(ids.map((id) => api.post(`/engagements/${engId}/documents/${id}/summarise/estimate`).then((r) => r.data)));
      setEstimate({ items, running: false });
    } catch (e) { toast.error(toErrMsg(e, t("docsum.failed"))); }
    finally { setBusy(false); }
  };
  const runSummaries = async () => {
    const items = asArray(estimate?.items).filter((i) => !i.too_large);
    setEstimate((s) => ({ ...s, running: true }));
    let ok = 0;
    for (const it of items) {
      // Batch 27 · Add-on 2 Step 2 · Q3: each summary runs as a background job; the screen follows it
      try {
        const r = await api.post(`/engagements/${engId}/documents/${it.document_id}/summarise`);
        const j = r.data?.job_id ? await waitForJob(r.data.job_id) : { status: "done" };
        if (j?.status === "done") ok += 1;
        else toast.error(`${it.filename}: ${jobErrorText(j, lang, t, t("docsum.failed"))}`);
      }
      catch (e) { if (e?.response?.status !== 402) toast.error(`${it.filename}: ${toErrMsg(e, t("docsum.failed"))}`); }
    }
    setEstimate(null); setChecked(new Set());
    if (ok) toast.success(t("docsum.done"));
    await load();
  };
  const decide = async (id, action) => {
    try { await api.post(`/engagements/${engId}/doc-summaries/${id}/decide`, { action }); await load(); }
    catch (e) { toast.error(toErrMsg(e, t("docsum.failed"))); }
  };
  const delSummary = async (id) => {
    if (!await askConfirm(t("docsum.deleteSummaryConfirm"))) return;
    try { await api.delete(`/engagements/${engId}/doc-summaries/${id}`); toast.success(t("docsum.binned")); await load(); }
    catch (e) { toast.error(toErrMsg(e, t("docsum.failed"))); }
  };
  const delDoc = async (d) => {
    if (!await askConfirm(t("docsum.deleteDocConfirm"))) return;
    try { await api.delete(`/documents/${d.id}`); toast.success(t("docsum.binned")); if (selId === d.id) setSelId(""); await load(); }
    catch (e) { toast.error(toErrMsg(e, t("docsum.failed"))); }
  };
  const openPage = async (docId, page) => {
    try {
      setViewer({ docId, page, url: `/documents/file/${docId}?mode=preview#page=${page}` });
    } catch (e) { toast.error(toErrMsg(e, t("docsum.failed"))); }
  };
  const exportOne = (s) => downloadBlob(`/engagements/${engId}/doc-summaries/export`, { summary_id: s.id, format: "pdf", lang }, "summary.pdf").catch((e) => toast.error(toErrMsg(e, t("docsum.failed"))));
  const exportAll = (format) => downloadBlob(`/engagements/${engId}/doc-summaries/export`, { format, lang }, `document_summaries.${format}`).catch((e) => toast.error(toErrMsg(e, t("docsum.failed"))));
  const toggle = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  const estTotal = asArray(estimate?.items).reduce((a, i) => a + (i.too_large ? 0 : i.estimated_units), 0);
  const estPages = asArray(estimate?.items).reduce((a, i) => a + i.pages, 0);

  return (
    <div className="space-y-4" data-testid="docsum-page">
      <LockedBanner engagement={current} />
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h1 className="text-xl font-semibold">{t("docsum.pageTitle")}</h1>
          <p className="text-xs text-muted-foreground">{t("docsum.pageSubtitle")}</p>
        </div>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" multiple accept={DOC_REVIEW_ACCEPT} className="hidden" onChange={(e) => uploadMany(e.target.files)} data-testid="docsum-upload-input" />
          <Button size="sm" variant="outline" disabled={locked || uploading} onClick={() => fileRef.current?.click()} data-testid="docsum-upload-btn">
            {uploading ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <Upload className="me-1 h-4 w-4" />}{t("docsum.upload")}
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={locked || busy || checked.size === 0} onClick={() => openEstimate(Array.from(checked))} data-testid="docsum-summarise-selected">
            <Sparkles className="me-1 h-4 w-4" />{t("docsum.summariseSelected")} {checked.size ? `(${checked.size})` : ""}
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportAll("pdf")} data-testid="docsum-export-all-pdf"><Download className="me-1 h-4 w-4" />{t("docsum.exportAllPdf")}</Button>
          <Button size="sm" variant="outline" onClick={() => exportAll("xlsx")} data-testid="docsum-export-all-xlsx"><Download className="me-1 h-4 w-4" />{t("docsum.exportAllXlsx")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2 rounded border bg-white" data-testid="docsum-doc-list">
          <div className="flex items-center gap-2 border-b p-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input className="h-8 text-xs" placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="docsum-search" />
            <select className="h-8 rounded border bg-white px-2 text-xs" value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="docsum-status-filter">
              <option value="all">{t("docsum.all")}</option><option value="none">{t("docsum.statusNone")}</option>
              <option value="draft">{t("docsum.draft")}</option><option value="accepted">{t("docsum.accepted")}</option>
            </select>
          </div>
          {filtered.length === 0 && <p className="p-4 text-xs text-muted-foreground" data-testid="docsum-no-docs">{t("docsum.noDocs")}</p>}
          <ul className="max-h-[70vh] divide-y overflow-y-auto">
            {filtered.map((d) => (
              <li key={d.id} className={`flex cursor-pointer items-start gap-2 p-2 text-xs hover:bg-slate-50 ${selId === d.id ? "bg-sky-50" : ""}`}
                  onClick={() => setSelId(d.id)} data-testid={`docsum-doc-${d.id}`}>
                <Checkbox checked={checked.has(d.id)} onCheckedChange={() => toggle(d.id)} onClick={(e) => e.stopPropagation()} className="mt-0.5" data-testid={`docsum-check-${d.id}`} />
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <bdi className="break-words font-medium" title={d.original_filename || d.filename}>{d.original_filename || d.filename}</bdi>
                    <span className="font-mono text-[10px] text-muted-foreground">{d.doc_ref}</span>
                    {d.duplicate_of && <span className="rounded bg-amber-100 px-1.5 text-[10px] text-amber-800" data-testid={`docsum-duplicate-${d.id}`}>{t("docsExtras.duplicateBadge").replace("{ref}", d.duplicate_of)}</span>}
                    <span className={`rounded px-1.5 text-[10px] ${STATUS_CLS[d.summary_status]}`} data-testid={`docsum-doc-status-${d.id}`}>
                      {d.summary_status === "none" ? t("docsum.statusNone") : d.summary_status === "draft" ? t("docsum.draft") : t("docsum.accepted")}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {d.doc_type || "—"}{d.page_count ? ` · ${d.page_count} ${t("docsum.pages")}` : ""} · <bdi>{d.uploaded_by_name || "—"}</bdi> · {fmtDateTime(d.created_at)}
                  </div>
                </div>
                {!locked && (
                  <button className="rounded p-1 text-rose-600 hover:bg-rose-50" title={t("docsum.deleteDoc")} onClick={(e) => { e.stopPropagation(); delDoc(d); }} data-testid={`docsum-doc-delete-${d.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3 space-y-3" data-testid="docsum-detail">
          {!sel && <div className="rounded border bg-white p-6 text-sm text-muted-foreground" data-testid="docsum-pick-hint">{t("docsum.pickHint")}</div>}
          {sel && (
            <>
              <div className="flex flex-wrap items-center gap-2 rounded border bg-white p-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold" title={sel.original_filename || sel.filename}><bdi>{sel.original_filename || sel.filename}</bdi> <span className="font-mono text-xs text-muted-foreground">{sel.doc_ref}</span></div>
                  <div className="text-[11px] text-muted-foreground">{selSums.length} {t("docsum.versions")}</div>
                </div>
                <div className="ms-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openPage(sel.id, 1)} data-testid="docsum-open-doc"><ExternalLink className="me-1 h-3.5 w-3.5" />{t("docsum.openDoc")}</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={locked || busy} onClick={() => openEstimate([sel.id])} data-testid="docsum-summarise">
                    {busy ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="me-1 h-3.5 w-3.5" />}{t("docsum.summarise")}
                  </Button>
                </div>
              </div>
              {selSums.length === 0 && <p className="rounded border bg-white p-4 text-xs text-muted-foreground" data-testid="docsum-empty">{t("docsum.empty")}</p>}
              {selSums.map((r) => (
                <div key={r.id} className={`rounded border bg-white p-3 text-sm ${r.status === "accepted" ? "border-emerald-200" : "border-amber-200"}`} data-testid={`docsum-row-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-mono font-semibold">V{r.version}</span>
                    <span className={`rounded px-1.5 text-[10px] ${STATUS_CLS[r.status === "accepted" ? "accepted" : "draft"]}`} data-testid={`docsum-status-${r.id}`}>
                      {r.status === "accepted" ? t("docsum.accepted") : t("docsum.draft")}
                    </span>
                    {r.status !== "accepted" && <AILabel />}
                    <span className="text-muted-foreground">
                      <bdi>{r.created_by}</bdi> · {fmtDateTime(r.created_at)} · {r.page_count} {t("docsum.pages")} · {Number(r.units || 0).toFixed(2)} {t("docsum.units")}
                      {r.accepted_at && <> · {t("docsum.acceptedBy")} <bdi>{r.accepted_by}</bdi> · {fmtDateTime(r.accepted_at)}</>}
                    </span>
                    <span className="ms-auto flex gap-1">
                      {!locked && r.status !== "accepted" && (
                        <>
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => decide(r.id, "accept")} data-testid={`docsum-accept-${r.id}`}><Check className="me-1 h-3 w-3" />{t("docsum.accept")}</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-rose-700" onClick={() => decide(r.id, "reject")} data-testid={`docsum-reject-${r.id}`}><X className="me-1 h-3 w-3" />{t("docsum.reject")}</Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => exportOne(r)} data-testid={`docsum-export-${r.id}`}><Download className="me-1 h-3 w-3" />{t("docsum.exportPdf")}</Button>
                      {!locked && <button className="rounded p-1 text-rose-600 hover:bg-rose-50" title={t("docsum.deleteSummary")} onClick={() => delSummary(r.id)} data-testid={`docsum-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                    </span>
                  </div>
                  {r.title && <div className="mt-2 font-semibold"><bdi>{r.title}</bdi></div>}
                  <p className="mt-1 text-sm" dir="auto">{r.summary}</p>
                  <ul className="mt-2 list-disc space-y-1 ps-5 text-sm">
                    {asArray(r.points).map((p, i) => (
                      <li key={i}><bdi>{p.text}</bdi>{" "}
                        {asArray(p.pages).map((pg) => (
                          <button key={pg} className="me-1 text-sky-700 underline-offset-2 hover:underline" onClick={() => openPage(r.document_id, pg)} data-testid={`docsum-page-link-${r.id}-${pg}`}>[p.{pg}]</button>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <Dialog open={!!estimate} onOpenChange={(o) => { if (!o && !estimate?.running) setEstimate(null); }}>
        <DialogContent data-testid="docsum-estimate-dialog">
          <DialogHeader><DialogTitle>{t("docsum.confirmTitle")}</DialogTitle></DialogHeader>
          <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
            {asArray(estimate?.items).map((i) => (
              <li key={i.document_id} className="flex justify-between gap-2 rounded border p-2">
                <bdi className="truncate" title={i.filename}>{i.filename}</bdi>
                <span className="shrink-0 text-muted-foreground">
                  {i.too_large ? t("docsum.tooLarge").replace("{max}", String(i.max_pages)) : `${i.pages} ${t("docsum.pages")}${i.scanned_pages ? ` (${i.scanned_pages} OCR)` : ""} · ~${i.estimated_units.toFixed(2)} ${t("docsum.units")}`}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-sm font-medium" data-testid="docsum-estimate-total">{t("docsum.estimateTotal").replace("{pages}", String(estPages)).replace("{units}", estTotal.toFixed(2))}</p>
          <p className="text-xs text-muted-foreground">{t("docsum.draftNote")}</p>
          <DialogFooter>
            <Button variant="outline" disabled={estimate?.running} onClick={() => setEstimate(null)} data-testid="docsum-estimate-cancel">{t("common.cancel")}</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={estimate?.running || estTotal === 0 && !asArray(estimate?.items).some((i) => !i.too_large)} onClick={runSummaries} data-testid="docsum-estimate-continue">
              {estimate?.running ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : null}{t("docsum.continue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-5xl h-[85vh]" data-testid="docsum-viewer">
          <DialogHeader><DialogTitle>{t("docsum.page")} {viewer?.page}</DialogTitle></DialogHeader>
          {viewer && <PdfJsViewer url={viewer.url} page={viewer.page} testId="docsum-viewer-iframe" />}
          <div className="text-xs"><Link to={`/documents?highlight=${viewer?.docId}`} className="text-sky-700 hover:underline" data-testid="docsum-open-review">{t("docsum.openInReview")}</Link></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
